import {
  DEFAULT_FILTER_INPUT_NUMBER_MAX_VALUE,
  DEFAULT_FILTER_INPUT_NUMBER_MIN_VALUE,
  FilterLogicsType,
  FilterOperatorsType,
} from 'constants/filters';
import {
  numericFilterTypes,
  TableFilterModel,
  TableFilterOptionModel,
  TableFilterSubtypeType,
  TableFilterType,
} from 'components/organisms/TableFilter/models';
import { PaginationFiltersCapabilityModel } from 'core/utils/searchFilters/models';
import { FilterConditionModel, FilterModel, TableFilterBasicModel, TableFilterBasicOptionModel } from './globalModels';
import { stringToCamelCase, stringToSnakeCase } from './strings';
import { DEFAULT_LOCALE } from 'constants/locales';

export const parseFiltersConditions = (filters: string[]): FilterConditionModel[] => {
  const filterConditions: FilterConditionModel[] = [];

  filters.forEach((filterString) => {
    const [filterField, filterValue] = filterString.split(FilterOperatorsType.EQUAL);

    filterConditions.push({
      field: stringToSnakeCase(filterField.trim()),
      operator: FilterOperatorsType.EQUAL,
      value: filterValue?.trim() || '',
    });
  });

  return filterConditions;
};

export const parseFiltersToBeSent = (filterValue: string, defaultFilters?: FilterConditionModel[]): FilterModel | undefined => {
  const filters = filterValue.split(FilterLogicsType.OR);
  const parsedConditions: FilterConditionModel[] = parseFiltersConditions(filters);
  const conditions = defaultFilters ? [...parsedConditions, ...defaultFilters] : parsedConditions;

  return { logic: FilterLogicsType.OR, conditions };
};

export const filterSubtypeManager = {
  [TableFilterSubtypeType.EQUAL]: FilterOperatorsType.EQUAL,
  [TableFilterSubtypeType.MAX]: FilterOperatorsType.LESS_THAN,
  [TableFilterSubtypeType.MAX_OR_EQUAL]: FilterOperatorsType.LESS_OR_EQUAL_THAN,
  [TableFilterSubtypeType.MIN]: FilterOperatorsType.GREATER_THAN,
  [TableFilterSubtypeType.MIN_OR_EQUAL]: FilterOperatorsType.GREATER_OR_EQUAL_THAN,
};

const getSelectedFilterObjectOptions = ({
  options,
  key,
  subKey,
  value,
  exactValue,
}: {
  options: FilterConditionModel[];
  key: string;
  subKey: string;
  value: string;
  exactValue: boolean;
}): FilterConditionModel | undefined => {
  const selectedOption = options.find(({ field, operator, value: optionValue }) => {
    const parsedField = stringToCamelCase(field);
    const isSameValue = value === optionValue;
    const isSameOperator = subKey === operator;
    const isDataChecked = exactValue ? isSameValue : isSameOperator;
    return parsedField === key && isDataChecked;
  });

  return selectedOption;
};

const getCurrentFilterOptions = (currentFilters: string): string[] => {
  const currentFilterOptions = currentFilters.split(/[,|]/);
  const filter = { field: '' };
  const currentFiltersArray: string[] = [];

  currentFilterOptions.forEach((currentFilterOption) => {
    const hasFilterOptionField = currentFilterOption.includes('=');

    if (hasFilterOptionField) {
      filter.field = currentFilterOption.split('=')[0];
      currentFiltersArray.push(currentFilterOption);
    }

    !hasFilterOptionField && currentFiltersArray.push(`${filter.field}=${currentFilterOption}`);
  });

  return currentFiltersArray;
};

const parseFilterValuesToBeChecked = ({ subKey, currentFilters }: { subKey: string; currentFilters: string }) => {
  const currentFilterOptions = getCurrentFilterOptions(currentFilters);
  const currentFilterObjectsOptions = parseFiltersConditions(currentFilterOptions);
  const parsedSubKey = filterSubtypeManager[subKey as TableFilterSubtypeType];

  return { options: currentFilterObjectsOptions, parsedSubKey };
};

export const checkIfOptionIsAlreadySelected = ({
  key,
  subKey,
  value,
  currentFilters,
  exactValue,
}: {
  key: string;
  subKey: string;
  value: string;
  currentFilters: string;
  exactValue: boolean;
}) => {
  const { options, parsedSubKey } = parseFilterValuesToBeChecked({ subKey, currentFilters });
  const selectedOption = getSelectedFilterObjectOptions({ options, key, subKey: parsedSubKey, value, exactValue });

  return !!selectedOption;
};

const getInitialFilter = (field: string, initialFilters: TableFilterModel[]): TableFilterModel | undefined => {
  return initialFilters.find((initialFilter) => initialFilter.key === field);
};

const getSelectedOption = (operator: FilterOperatorsType, options: TableFilterOptionModel[]): TableFilterOptionModel | undefined => {
  return options.find((option) => option.operator === operator);
};

export const getSelectedOptionValueFromStringFilters = ({
  key,
  subKey,
  currentFilters,
}: {
  key: string;
  subKey: string;
  currentFilters: string;
}) => {
  const { options, parsedSubKey } = parseFilterValuesToBeChecked({ subKey, currentFilters });
  const selectedOption = getSelectedFilterObjectOptions({ options, key, subKey: parsedSubKey, value: '', exactValue: false });

  return !!selectedOption ? String(selectedOption.value) : '';
};

export const checkValueErrorFormat = (option: string, filters: TableFilterModel[]): boolean => {
  const [{ field, value }] = parseFiltersConditions([option]);
  const initialFilter = getInitialFilter(field, filters);
  const isNumericType = !!initialFilter && numericFilterTypes.includes(initialFilter.type);

  return isNumericType && isNaN(Number(value));
};

export const checkValueOutsideRange = (option: string, filters: TableFilterModel[]) => {
  const [{ field, operator, value }] = parseFiltersConditions([option]);
  const initialFilter = getInitialFilter(field, filters);
  const selectedOption = !!initialFilter ? getSelectedOption(operator, initialFilter.options) : undefined;

  if (!!selectedOption) {
    const isDateRange = selectedOption.type === TableFilterType.DATE_RANGE;
    const defaultMaxValue = isDateRange ? Date.now() : DEFAULT_FILTER_INPUT_NUMBER_MAX_VALUE;
    const min = selectedOption.min || DEFAULT_FILTER_INPUT_NUMBER_MIN_VALUE;
    const max = selectedOption.max || defaultMaxValue;
    const isUnderMinValue = Number(value) < min;
    const isOverMaxValue = Number(value) > max;

    return isUnderMinValue || isOverMaxValue;
  }

  return true;
};

export const checkFilterOptionError = (option: string, filters: TableFilterModel[]): boolean => {
  const isFormatErrorValue = checkValueErrorFormat(option, filters);
  const isOutsideRangeValues = checkValueOutsideRange(option, filters);

  return isFormatErrorValue || isOutsideRangeValues;
};

const setTableFilterCheckboxOptions = ({
  key,
  label,
  value,
  selected,
  currentFilters,
  type,
  subtype,
  operator,
  defaultValue,
  error,
  errorMessage,
}: TableFilterBasicOptionModel): TableFilterOptionModel => {
  const parsedSelected: boolean = checkIfOptionIsAlreadySelected({
    key: key,
    subKey: TableFilterSubtypeType.EQUAL,
    value: String(value),
    currentFilters: currentFilters || '',
    exactValue: true,
  });

  return {
    key: `${key}:${value}`,
    type: type || TableFilterType.CHECKBOX,
    subtype: subtype || TableFilterSubtypeType.EQUAL,
    label,
    operator: operator || FilterOperatorsType.EQUAL,
    selected: selected || parsedSelected,
    defaultValue: defaultValue ? String(defaultValue) : String(value),
    value: String(value),
    error: error || false,
    errorMessage: errorMessage || '',
    disabled: false,
  };
};

const setTableFilterOptionsManager = {
  [TableFilterType.CHECKBOX]: (values: TableFilterBasicOptionModel) => setTableFilterCheckboxOptions(values),
  [TableFilterType.DATE]: (values: TableFilterBasicOptionModel) => setTableFilterCheckboxOptions(values),
  [TableFilterType.DATE_RANGE]: (values: TableFilterBasicOptionModel) => setTableFilterCheckboxOptions(values),
  [TableFilterType.INPUT]: (values: TableFilterBasicOptionModel) => setTableFilterCheckboxOptions(values),
  [TableFilterType.INPUT_NUMBER]: (values: TableFilterBasicOptionModel) => setTableFilterCheckboxOptions(values),
  [TableFilterType.RANGE]: (values: TableFilterBasicOptionModel) => setTableFilterCheckboxOptions(values),
};

export const getFilters = ({
  items,
  options,
  currentFilters,
}: {
  items: TableFilterBasicModel[];
  options: TableFilterBasicOptionModel[];
  currentFilters: string;
}): TableFilterModel[] => {
  return items.map(({ key, label, type }) => {
    const parsedOptions =
      options
        .filter((option) => option.key === key)
        .sort((a, b) => a.label.localeCompare(b.label, DEFAULT_LOCALE))
        .map((item) => setTableFilterOptionsManager[type]({ ...item, currentFilters })) || [];

    return { key, label, type, options: parsedOptions };
  });
};

export const checkValidUrlFilters = ({ urlFilter, dataFilters }: { urlFilter: string; dataFilters: PaginationFiltersCapabilityModel }) => {
  const defaultFiltersKeys = dataFilters ? Object.keys(dataFilters) : [];

  return urlFilter.split(FilterLogicsType.OR).filter((filterKeys) => {
    const filterValueKey = filterKeys.split('=')[0];

    return defaultFiltersKeys.includes(filterValueKey);
  });
};

export const checkUrlFilterKeysAreValid = ({
  urlFilter,
  dataFilters,
}: {
  urlFilter: string;
  dataFilters: PaginationFiltersCapabilityModel;
}): boolean => {
  const validUrlFilterKeys = checkValidUrlFilters({ urlFilter, dataFilters });
  const validUrlFilterKeysString = validUrlFilterKeys.join(FilterLogicsType.OR);

  return urlFilter === validUrlFilterKeysString;
};
